import React from "react";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/src/components/ui/tabs";
import useStuckAttribute from "@/src/hooks/useStuckAttribute";
import { cn } from "@/src/lib/utils";

import {
  NAV_TABS_BAR_CLASSES,
  NAV_TABS_LIST_CLASSES,
  NAV_TABS_TRIGGER_CLASSES,
} from "./navTabsStyles";

import type { JSX, ReactNode } from "react";

export interface NavTabItem {
  value: string;
  label: string;
  content: ReactNode;
}

interface NavTabsProps {
  tabList: readonly NavTabItem[];
  value: string;
  onValueChange: (value: string) => void;
  // Controls shared by every tab (e.g. filters); pages without tabs keep them in their header
  actions?: ReactNode;
}

// Sticky so actions stay reachable on long pages
function NavTabs({
  tabList,
  value,
  onValueChange,
  actions,
}: NavTabsProps): JSX.Element {
  const stickyRef = useStuckAttribute<HTMLDivElement>();

  return (
    <Tabs
      className="@container/nav-tabs gap-6"
      onValueChange={onValueChange}
      value={value}
    >
      <div
        ref={stickyRef}
        className={cn(
          NAV_TABS_BAR_CLASSES,
          "flex flex-col @4xl/nav-tabs:flex-row @4xl/nav-tabs:items-center @4xl/nav-tabs:justify-between @4xl/nav-tabs:gap-6",
        )}
      >
        <TabsList className={NAV_TABS_LIST_CLASSES} variant="line">
          {tabList.map((tab) => (
            <TabsTrigger
              key={tab.value}
              className={NAV_TABS_TRIGGER_CLASSES}
              value={tab.value}
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {actions ? (
          <div className="border-t py-2 @4xl/nav-tabs:border-t-0 @4xl/nav-tabs:py-0">
            {actions}
          </div>
        ) : null}
      </div>
      {tabList.map((tab) => (
        <TabsContent
          key={tab.value}
          className="@container min-w-0"
          value={tab.value}
        >
          {tab.content}
        </TabsContent>
      ))}
    </Tabs>
  );
}

export default NavTabs;
